import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Icon, Logo } from '../../design/Icon';
import { ThemeToggle } from '../../components/shared/Button';
import { useToast } from '../../components/shared/Overlay';
import { useShellStatus } from '../../layout';
import { usePolling } from '../../hooks/usePolling';
import { onboardingAPI, onboardingConfigAPI } from '../../services/onboardingApi';
import {
  HISTORY_DAYS,
  STEPS,
  checkErrorFromValidate,
  connectErrorFromStart,
  consentOutcome,
  csvErrorReport,
  csvErrorSummary,
  formFromStatus,
  importResumeState,
  mappingForRow,
  mappingRows,
  onboardingShortName,
  parseInvites,
  resumeErrorFrom,
  rowChanged,
  stepForStatus,
  storedValidation,
  topDivision,
  upnDomain,
} from './onboardingLogic';
import { ApproveStep, CheckStep, ConnectStep, FinishStep, ImportStep, OrganizationStep, OrganizeStep } from './OnboardingSteps';
import './OnboardingPage.css';

const ONBOARDING_ID_KEY = 'vertolytics.onboardingId';
const IMPORT_POLL_MS = 3000;

const readStoredId = () => {
  try {
    return window.sessionStorage.getItem(ONBOARDING_ID_KEY);
  } catch (e) {
    return null;
  }
};

const storeId = (id) => {
  try {
    window.sessionStorage.setItem(ONBOARDING_ID_KEY, id);
  } catch (e) {
    // Storage blocked: auth/me returns the onboarding id on the next visit.
  }
};

const messageOf = (err, fallback) => err?.response?.data?.message || fallback;

// Self-service onboarding wizard, a port of the approved docs/design/mockups/onboarding.html (bare layout, no nav).
// The wizard never sends email; each step says what happens next on screen.
export function OnboardingPage() {
  const { user, logout, retryProfile } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();

  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [furthest, setFurthest] = useState(0);
  const [onboardingId, setOnboardingId] = useState(null);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(() => formFromStatus(null, user));
  const [formErrors, setFormErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const [method, setMethod] = useState('consent');
  const [connectError, setConnectError] = useState(null);
  const [consentError, setConsentError] = useState(null);
  const [credentials, setCredentials] = useState({ clientId: '', clientSecret: '' });
  const [credentialError, setCredentialError] = useState(null);

  const [checks, setChecks] = useState(null);
  const [checksRunning, setChecksRunning] = useState(false);
  const [checksError, setChecksError] = useState(null);

  const [levels, setLevels] = useState([]);
  const [levelName, setLevelName] = useState('Division');
  const [nodes, setNodes] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [rows, setRows] = useState([]);
  const [organizeError, setOrganizeError] = useState(null);
  const [csvReport, setCsvReport] = useState(null);
  const fileInputRef = useRef(null);

  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState(null);

  const [invites, setInvites] = useState('');
  const [inviteError, setInviteError] = useState(null);

  useEffect(() => {
    document.title = 'Set up Vertolytics | Vertolytics';
  }, []);

  const goTo = useCallback((next) => {
    setStep(next);
    setFurthest((f) => Math.max(f, next));
    window.scrollTo(0, 0);
  }, []);

  const loadStatus = useCallback(async (id) => {
    const next = await onboardingAPI.getStatus(id);
    setStatus(next);
    return next;
  }, []);

  const runChecks = useCallback(
    async (id) => {
      setChecksRunning(true);
      setChecksError(null);
      try {
        const result = await onboardingAPI.validate(id);
        setChecks(result);
        await loadStatus(id);
      } catch (err) {
        setChecksError(checkErrorFromValidate(err));
      } finally {
        setChecksRunning(false);
      }
    },
    [loadStatus]
  );

  useEffect(() => {
    let cancelled = false;
    const consent = consentOutcome(window.location.search);
    if (consent) window.history.replaceState(null, '', window.location.pathname);

    (async () => {
      const id = user?.onboardingId || readStoredId();
      let current = null;
      if (id) {
        try {
          current = await onboardingAPI.getStatus(id);
        } catch (e) {
          current = null;
        }
      }
      if (cancelled) return;

      if (!current) {
        setLoading(false);
        return;
      }

      setOnboardingId(current.id);
      storeId(current.id);
      setStatus(current);
      setForm(formFromStatus(current, user));

      let next = stepForStatus(current);
      if (next === 6 && current.currentStep === 'OnboardingCompleted') {
        navigate('/overview', { replace: true });
        return;
      }
      if (consent && !consent.approved) {
        setConsentError(consent);
        next = 2;
      }
      const stored = storedValidation(current);
      if (stored) setChecks(stored);
      setStep(next);
      setFurthest(next);
      setLoading(false);
      if (next === 3 && (consent?.approved || !stored)) runChecks(current.id);
    })();

    return () => {
      cancelled = true;
    };
    // Runs once on arrival (including the return from Microsoft's consent page).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { orgName: accountShortName, terms } = useShellStatus();
  const orgName = status?.companyName || form.companyName || 'your organization';
  const shortName = onboardingShortName(status, accountShortName, orgName);

  /* ---------- Step 1: organization ---------- */
  const startOnboarding = async () => {
    const errors = {};
    if (!form.companyName.trim()) errors.companyName = 'Enter your organization name.';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errors.email = 'Enter a work email address.';
    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    setBusy(true);
    try {
      const started = await onboardingAPI.start({
        companyName: form.companyName.trim(),
        contactName: form.contactName.trim(),
        email: form.email.trim(),
        timeZone: form.timeZone,
        industry: form.industry,
        estimatedMonthlyCalls: form.estimatedMonthlyCalls,
      });
      setOnboardingId(started.onboardingId);
      storeId(started.onboardingId);
      const current = await loadStatus(started.onboardingId);
      setConnectError(null);
      const resumeAt = stepForStatus(current);
      goTo(resumeAt > 1 ? resumeAt : 1);
    } catch (err) {
      setConnectError(connectErrorFromStart(err));
      goTo(1);
    } finally {
      setBusy(false);
    }
  };

  /* ---------- Steps 2 and 3: connect and approve ---------- */
  const continueToApprove = async () => {
    if (!onboardingId) {
      await startOnboarding();
      return;
    }
    goTo(2);
  };

  const openConsent = async () => {
    setBusy(true);
    try {
      const { consentUrl } = await onboardingAPI.getConsentUrl(onboardingId);
      window.location.assign(consentUrl);
    } catch (err) {
      setConsentError({ title: "The approval couldn't start", body: messageOf(err, 'Vertolytics could not create the approval link. Try again.') });
      setBusy(false);
    }
  };

  const copyConsentLink = async () => {
    setBusy(true);
    try {
      const { consentUrl } = await onboardingAPI.getConsentUrl(onboardingId);
      await navigator.clipboard.writeText(consentUrl);
      showToast('Approval link copied. It works for 15 minutes.');
    } catch (err) {
      showToast("The approval link couldn't be copied", 'alert');
    } finally {
      setBusy(false);
    }
  };

  const saveCredentials = async () => {
    if (!credentials.clientId.trim() || !credentials.clientSecret) {
      setCredentialError('Enter the client ID and a client secret.');
      return;
    }
    setBusy(true);
    setCredentialError(null);
    try {
      await onboardingAPI.configureGraph(onboardingId, { clientId: credentials.clientId.trim(), clientSecret: credentials.clientSecret });
      setCredentials({ clientId: credentials.clientId.trim(), clientSecret: '' });
      await loadStatus(onboardingId);
      goTo(3);
      runChecks(onboardingId);
    } catch (err) {
      setCredentialError(messageOf(err, "The credentials couldn't be saved."));
    } finally {
      setBusy(false);
    }
  };

  /* ---------- Step 5: organize ---------- */
  const nodesById = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes]);
  const divisions = useMemo(() => {
    const top = [...levels].sort((a, b) => a.order - b.order)[0];
    return nodes.filter((n) => n.isActive !== false && (top ? n.levelId === top.id : !n.parentId)).sort((a, b) => a.name.localeCompare(b.name));
  }, [levels, nodes]);
  const activeCommunities = useMemo(() => communities.filter((c) => c.isActive !== false).sort((a, b) => a.name.localeCompare(b.name)), [communities]);
  const divisionOf = useCallback((nodeId) => topDivision(nodeId, nodesById), [nodesById]);

  const loadOrganize = useCallback(async () => {
    setOrganizeError(null);
    try {
      const [levelList, nodeList, communityList, mappings, suggestions] = await Promise.all([
        onboardingConfigAPI.getLevels(),
        onboardingConfigAPI.getNodes(),
        onboardingConfigAPI.getCommunities(),
        onboardingConfigAPI.getMappings(),
        onboardingConfigAPI.getSuggestions(),
      ]);
      const sorted = [...levelList].sort((a, b) => a.order - b.order);
      setLevels(sorted);
      setLevelName(sorted[0]?.displayName || 'Division');
      setNodes(nodeList);
      setCommunities(communityList);
      setRows(mappingRows(mappings, suggestions, nodeList, communityList));
    } catch (err) {
      setOrganizeError(messageOf(err, 'Vertolytics could not load the hierarchy and mappings. Reload the page to try again.'));
    }
  }, []);

  useEffect(() => {
    if (step === 4 && !loading) loadOrganize();
  }, [step, loading, loadOrganize]);

  const updateRow = (index, change) => setRows((list) => list.map((row, i) => (i === index ? { ...row, ...change, nameMatch: false } : row)));

  const uploadCommunities = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const preview = await onboardingConfigAPI.importCommunities(text, true);
      const summary = csvErrorSummary(preview, file.name, terms);
      if (summary) {
        setCsvReport({ ...summary, report: preview });
        return;
      }
      const applied = await onboardingConfigAPI.importCommunities(text, false);
      setCsvReport(null);
      showToast(`Imported ${terms.count(applied.created + applied.updated)} from ${file.name}`);
      await loadOrganize();
    } catch (err) {
      setCsvReport({ title: `${file.name} couldn't be imported`, body: messageOf(err, `Check that the file uses the ${terms.communityLower} template columns.`), report: null });
    } finally {
      setBusy(false);
    }
  };

  const downloadCsvErrors = () => {
    if (!csvReport?.report) return;
    const url = URL.createObjectURL(new Blob([csvErrorReport(csvReport.report)], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'community-import-errors.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const saveAndImport = async () => {
    setBusy(true);
    setOrganizeError(null);
    try {
      const name = levelName.trim();
      if (name && levels.length > 0 && levels[0].displayName !== name) {
        const saved = await onboardingConfigAPI.saveLevels(levels.map((l, i) => (i === 0 ? { ...l, displayName: name, pluralDisplayName: `${name}s` } : l)));
        setLevels(saved);
      }
      for (const row of rows.filter(rowChanged)) await onboardingConfigAPI.updateMapping(mappingForRow(row));

      const current = await loadStatus(onboardingId);
      if (current.currentStep === 'PermissionsValidated') {
        const from = new Date(Date.now() - HISTORY_DAYS * 24 * 3600 * 1000).toISOString();
        await onboardingAPI.startImport(onboardingId, from);
        await loadStatus(onboardingId);
      }
      goTo(5);
    } catch (err) {
      setOrganizeError(messageOf(err, 'The mappings could not be saved. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  /* ---------- Steps 6 and 7: import and finish ---------- */
  const importPolling = usePolling(() => onboardingAPI.getImportProgress(onboardingId), IMPORT_POLL_MS, {
    enabled: Boolean(onboardingId) && (step === 5 || step === 6) && !loading,
    deps: [onboardingId, step],
  });
  const progress = importPolling.data;

  const resumeImport = async () => {
    setResuming(true);
    setResumeError(null);
    try {
      await onboardingAPI.resumeImport(onboardingId);
      showToast('Import resumed from the first day not yet imported');
      await importPolling.refresh();
    } catch (err) {
      setResumeError(resumeErrorFrom(err));
    } finally {
      setResuming(false);
    }
  };

  const kpis = useMemo(() => {
    const entity = (type) => (progress?.entityProgress || []).find((e) => e.entityType === type)?.recordsImported || 0;
    const fmt = (n) => Number(n).toLocaleString('en-US');
    const level = (levels[0]?.pluralDisplayName || `${levelName}s`).toLowerCase();
    const range = progress?.importFromDate
      ? `${new Date(progress.importFromDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} to ${new Date(progress.importToDate || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      : '';
    return [
      { label: 'Calls imported', value: fmt(entity('CallRecords')), meta: range },
      { label: terms.communities, value: fmt(activeCommunities.length), meta: `in ${divisions.length} ${level}` },
      { label: 'Agents found', value: fmt(entity('Agents')), meta: `across ${entity('Queues')} queues` },
    ];
  }, [progress, levels, levelName, activeCommunities, divisions, terms]);

  useEffect(() => {
    if (step === 6 && !loading && nodes.length === 0 && communities.length === 0) loadOrganize();
  }, [step, loading, nodes.length, communities.length, loadOrganize]);

  const finish = async (destination) => {
    const emails = parseInvites(invites);
    const typed = (invites || '').split(/[\s,;]+/).filter(Boolean);
    if (typed.length !== emails.length) {
      setInviteError('Enter email addresses separated by commas.');
      return;
    }
    setBusy(true);
    setInviteError(null);
    try {
      for (const email of emails) {
        try {
          await onboardingConfigAPI.createUser({ email, displayName: email.split('@')[0], roles: [], scopedCommunityIds: [], scopedHierarchyNodeIds: [] });
        } catch (err) {
          if (err?.response?.status !== 409) throw err;
        }
      }
      await onboardingAPI.complete(onboardingId);
      try {
        window.sessionStorage.removeItem(ONBOARDING_ID_KEY);
      } catch (e) {
        // Storage blocked; nothing to clean up.
      }
      retryProfile();
      navigate(destination, { replace: true });
    } catch (err) {
      setInviteError(messageOf(err, 'Setup could not be finished. Try again.'));
      setBusy(false);
    }
  };

  /* ---------- Layout ---------- */
  const stepHasError =
    (step === 1 && Boolean(connectError)) ||
    (step === 2 && Boolean(consentError)) ||
    (step === 3 && !checksRunning && (Boolean(checksError) || (checks && !checks.isValid))) ||
    (step === 4 && Boolean(csvReport || organizeError)) ||
    (step === 5 && Boolean(resumeError || (progress && (progress.stage === 'Failed' || importResumeState(progress) || (progress.isRetrying && (progress.warnings || []).length)))));

  const renderStep = () => {
    switch (step) {
      case 0:
        return <OrganizationStep form={form} onChange={setForm} errors={formErrors} busy={busy} onContinue={startOnboarding} />;
      case 1:
        return (
          <ConnectStep
            orgName={shortName}
            method={method}
            onMethod={setMethod}
            error={connectError}
            upn={user?.email}
            domain={upnDomain(user?.email)}
            onUseAnotherAccount={logout}
            busy={busy}
            onBack={() => goTo(0)}
            onContinue={continueToApprove}
          />
        );
      case 2:
        return (
          <ApproveStep
            orgName={shortName}
            method={method}
            error={consentError}
            credentials={credentials}
            onCredentials={setCredentials}
            credentialError={credentialError}
            tenantId={status?.tenantId}
            busy={busy}
            onBack={() => goTo(1)}
            onApprove={openConsent}
            onCopyLink={copyConsentLink}
            onSaveCredentials={saveCredentials}
          />
        );
      case 3:
        return (
          <CheckStep
            orgName={orgName}
            shortName={shortName}
            result={checks}
            running={checksRunning}
            requestError={checksError}
            onUseAnotherAccount={logout}
            onBack={() => goTo(2)}
            onRunAgain={() => runChecks(onboardingId)}
            onContinue={() => goTo(4)}
          />
        );
      case 4:
        return (
          <OrganizeStep
            orgName={shortName}
            levelName={levelName}
            onLevelName={setLevelName}
            rows={rows}
            divisions={divisions}
            communities={activeCommunities}
            csvError={csvReport}
            busy={busy}
            loadError={organizeError}
            fileInputRef={fileInputRef}
            onFile={uploadCommunities}
            onRow={updateRow}
            divisionOf={divisionOf}
            onDownloadErrors={downloadCsvErrors}
            onBack={() => goTo(3)}
            onSave={saveAndImport}
          />
        );
      case 5:
        return (
          <ImportStep
            progress={progress}
            loadError={importPolling.error && !progress ? messageOf(importPolling.error, 'Import progress could not load. It keeps running in the background.') : null}
            resuming={resuming}
            resumeError={resumeError}
            onResume={resumeImport}
            onBack={() => goTo(4)}
            onContinue={() => goTo(6)}
          />
        );
      default:
        return (
          <FinishStep
            orgName={orgName}
            shortName={shortName}
            kpis={kpis}
            invites={invites}
            onInvites={setInvites}
            inviteError={inviteError}
            busy={busy}
            onBack={() => goTo(5)}
            onSetRoles={() => finish('/admin/users')}
            onOpenOverview={() => finish('/overview')}
          />
        );
    }
  };

  return (
    <div className="ob">
      <header className="ob-top">
        <div className="brand">
          <Logo size={26} />
          Vertolytics
        </div>
        {(status?.companyName || (step > 0 && form.companyName.trim())) && <div className="ctx">Setting up {status?.companyName || form.companyName.trim()}</div>}
        <div style={{ marginLeft: 'auto' }} className="row">
          <ThemeToggle />
          <button className="btn btn-ghost" type="button" onClick={logout}>
            Save and exit
          </button>
        </div>
      </header>
      <div className="ob-body">
        <nav aria-label="Setup steps">
          <ol className="steps">
            {STEPS.map((s, i) => {
              const cls = i < step ? 'done' : i === step ? (stepHasError ? 'cur err' : 'cur') : '';
              return (
                <li key={s.label}>
                  <button className={`step ${cls}`} type="button" disabled={loading || i > furthest} aria-current={i === step ? 'step' : undefined} onClick={() => goTo(i)}>
                    <span className="b">{i < step ? <Icon name="check" /> : i === step && stepHasError ? '!' : i + 1}</span>
                    <span>
                      <span className="l" style={{ display: 'block' }}>
                        {s.termLabel ? s.termLabel.replace('{communities}', terms.communitiesLower) : s.label}
                      </span>
                      <span className="s">{s.sub}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>
        <section className="ob-card" aria-live="polite" aria-busy={loading || undefined}>
          {loading ? (
            <div className="ob-card-body">
              <div className="row" style={{ gap: 10 }}>
                <span className="spinner" />
                <span className="small sub">Loading setup…</span>
              </div>
            </div>
          ) : (
            renderStep()
          )}
        </section>
      </div>
    </div>
  );
}
